import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import {
  ActivityTypeTagsHorizontalBarChart,
  DayOfWeekBarChart,
  EntryTypesPieChart,
  EntryTypesPieChartProps,
  EntryTypesStackedBarChart,
  EntryTypesStackedBarChartProps,
  ErrorBoundary,
  HourOfDayBarChart,
  RPCs,
  RPCDocs,
  useRPC,
} from '@therms/react-modules'
import { Icon, Loading } from '@therms/atalaya'
import { MdAreaChart } from 'react-icons/md'

interface LocationLogEntriesHistogramsProps {
  endDate?: string
  locationAndGroupIds?: string[]
  startDate: string
}

function LocationLogEntriesHistograms({
  endDate = new Date().toISOString(),
  locationAndGroupIds,
  startDate,
}: LocationLogEntriesHistogramsProps) {
  const calendarInterval: 'day' | 'month' = 'day'

  const { data } = useRPC<
    RPCDocs.Stats.GetLocationLogEntriesHistogram.v1.Args,
    RPCDocs.Stats.GetLocationLogEntriesHistogram.v1.Data
  >(
    RPCs.Stats.GetLocationLogEntriesHistogram.v1,
    {
      calendarInterval: calendarInterval as any,
      fromDate: startDate,
      includeEntryTypes: true,
      locationAndGroupIds:
        locationAndGroupIds && locationAndGroupIds?.length > 0
          ? locationAndGroupIds
          : undefined,
      toDate: endDate,
    },
    {
      cacheStrategy: 'cache-only',
      timeout: 60_000,
      transport: 'http',
    },
  )

  const chartData = useMemo(() => {
    if (!data) return undefined

    const entryTypesAggMap = new Map<
      string,
      { color?: string; count: number; name: string }
    >()

    const entryTypesHistogram: EntryTypesStackedBarChartProps['entriesHistogram'] =
      Object.entries(data.histogram).map(([isoDate, { count, entryTypes }]) => {
        entryTypes.forEach((entryType) => {
          const entryTypeData = entryTypesAggMap.get(entryType.name) || {
            color: entryType.color,
            count: 0,
            name: entryType.name,
          }

          entryTypeData.count += entryType.count

          entryTypesAggMap.set(entryType.name, entryTypeData)
        })

        return {
          count,
          entryTypes,
          formattedDate:
            calendarInterval === 'day'
              ? dayjs(isoDate).format('M/D')
              : dayjs(isoDate).format('MMM'),
          isoDateString: isoDate,
        }
      })

    const entryTypeCount: EntryTypesPieChartProps['entryTypeCount'] =
      Array.from(entryTypesAggMap.values()).map(({ color, count, name }) => ({
        color,
        count,
        entryType: name,
      }))

    return {
      entryTypeCount,
      entryTypesHistogram,
    }
  }, [data])

  if (data && !data.totalCount) {
    return (
      <div className="mt-xxl flex flex-col items-center text-lg text-neutral">
        <Icon Icon={MdAreaChart} size="xxl" />
        <div>No Location Log Entry stats to show</div>
      </div>
    )
  }

  return (
    <div className="relative">
      {!chartData || !data ? (
        <div className="flex justify-center">
          <Loading size="lg" />
        </div>
      ) : (
        <div className="space-y-base">
          <ErrorBoundary>
            <EntryTypesStackedBarChart
              entriesHistogram={chartData.entryTypesHistogram}
            />
          </ErrorBoundary>

          <ErrorBoundary>
            <EntryTypesPieChart entryTypeCount={chartData.entryTypeCount} />
          </ErrorBoundary>

          <div className="flex flex-col">
            <ErrorBoundary>
              <DayOfWeekBarChart dowCount={data.dow as any} />
            </ErrorBoundary>

            <ErrorBoundary>
              <HourOfDayBarChart hourCount={data.hour as any} />
            </ErrorBoundary>
          </div>

          <ErrorBoundary>
            <ActivityTypeTagsHorizontalBarChart
              activityTypeTags={data.activityTypeTagCount}
            />
          </ErrorBoundary>
        </div>
      )}
    </div>
  )
}

export { LocationLogEntriesHistograms }
